// contentEditable tools

export function getVisualSelection(node) {
  const nodeDocument = null == node ? document : node.ownerDocument
  const nodeWindow = null == nodeDocument ? window : nodeDocument.defaultView
  if (null != node && nodeDocument.activeElement === node) {
    const selection = nodeWindow.getSelection()
    if (selection.rangeCount >= 1) {
      const range = selection.getRangeAt(0)
      const selectionStart = findVisualSelectionOffsetFromRangeNode(node, range.startContainer, range.startOffset)
      const selectionEnd = range.collapsed
        ? selectionStart
        : findVisualSelectionOffsetFromRangeNode(node, range.endContainer, range.endOffset)
      return [selectionStart, selectionEnd]
    }
  }
  return [null, null]
}

export function setVisualSelection(node, start, end = start) {
  const nodeDocument = null == node ? document : node.ownerDocument
  const nodeWindow = null == nodeDocument ? window : nodeDocument.defaultView
  if (null != node && nodeDocument.activeElement === node && start >= 0 && end >= 0) {
    const [normalizedStart, normalizedEnd] = end < start ? [end, start] : [start, end]
    const range = nodeDocument.createRange()
    const { node: rangeStartNode, offset: rangeStartOffset } = findRangeNodeForVisualSelectionOffset(
      node,
      normalizedStart
    )
    range.setStart(rangeStartNode, rangeStartOffset)
    if (normalizedEnd !== normalizedStart) {
      const { node: rangeEndNode, offset: rangeEndOffset } = findRangeNodeForVisualSelectionOffset(node, normalizedEnd)
      range.setEnd(rangeEndNode, rangeEndOffset)
    } else {
      range.setEnd(rangeStartNode, rangeStartOffset)
      range.collapse(true)
    }
    const selection = nodeWindow.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

export function getNodeVisualLength(node) {
  if (node.hasChildNodes()) {
    let nodeLength = 0
    const childNodes = node.childNodes
    for (const childNode of childNodes) {
      nodeLength += getNodeVisualLength(childNode)
    }
    return nodeLength
  } else {
    switch (node.nodeType) {
      case Node.TEXT_NODE: {
        return (node.textContent || '').length
      }
      case Node.ELEMENT_NODE: {
        // collapsed element
        switch (node.tagName ? node.tagName.toLowerCase() : '') {
          case 'span':
          case 'div': {
            return (node.textContent || '').length
          }
          default: {
            return Math.max(1, (node.textContent || '').length)
          }
        }
      }
      default: {
        return 0
      }
    }
  }
}

export function findVisualSelectionOffsetFromRangeNode(rootNode, rangeNode, rangeNodeOffset) {
  if (rootNode === rangeNode) {
    if (rootNode.hasChildNodes()) {
      let currentVisualOffset = 0
      const childNodes = rootNode.childNodes
      for (let i = 0; i < childNodes.length && i < rangeNodeOffset; i++) {
        currentVisualOffset += getNodeVisualLength(childNodes[i])
      }
      return currentVisualOffset
    } else {
      return rangeNodeOffset
    }
  } else if (rootNode.hasChildNodes()) {
    let currentVisualOffset = 0
    const searchPath = [rootNode.childNodes[0]]
    while (0 < searchPath.length) {
      const tailIndex = searchPath.length - 1
      const currentNode = searchPath[tailIndex]
      if (rangeNode === currentNode) {
        // rangeNode found
        return currentVisualOffset + findVisualSelectionOffsetFromRangeNode(currentNode, rangeNode, rangeNodeOffset)
      } else if (null == currentNode) {
        // at the end of children list
        searchPath.pop()
        const parentNodeIndex = searchPath.length - 1
        if (0 <= parentNodeIndex) {
          const parentNode = searchPath[parentNodeIndex]
          searchPath[parentNodeIndex] = null == parentNode ? null : parentNode.nextSibling
        }
      } else if (currentNode.hasChildNodes()) {
        // go deeper
        searchPath.push(currentNode.childNodes[0])
      } else {
        // count this node and move forward
        searchPath[tailIndex] = currentNode.nextSibling
        currentVisualOffset += getNodeVisualLength(currentNode)
      }
    }
    return currentVisualOffset
  } else {
    return getNodeVisualLength(rootNode)
  }
}

export function findRangeNodeForVisualSelectionOffset(rootNode, visualOffset) {
  if (0 === visualOffset) {
    return {
      node: rootNode,
      offset: 0
    }
  } else if (rootNode.hasChildNodes()) {
    let currentVisualOffset = visualOffset
    const searchPath = [rootNode.childNodes[0]]
    while (0 < searchPath.length) {
      const tailIndex = searchPath.length - 1
      const currentNode = searchPath[tailIndex]
      if (null == currentNode) {
        // at the end of children list
        searchPath.pop()
        const parentNodeIndex = searchPath.length - 1
        if (0 <= parentNodeIndex) {
          const parentNode = searchPath[parentNodeIndex]
          searchPath[parentNodeIndex] = null == parentNode ? null : parentNode.nextSibling
        }
      } else if (currentNode.hasChildNodes()) {
        // go deeper
        searchPath.push(currentNode.childNodes[0])
      } else {
        // count this node and possibly move forward
        const currentNodeLength = getNodeVisualLength(currentNode)
        if (0 > currentVisualOffset - currentNodeLength) {
          // offset inside node
          return findRangeNodeForVisualSelectionOffset(currentNode, currentVisualOffset)
        } else if (currentVisualOffset === currentNodeLength) {
          // offset after node
          searchPath.pop()
          const parentNodeIndex = searchPath.length - 1
          if (0 <= parentNodeIndex) {
            let currentOffset = 0
            const parentNode = searchPath[parentNodeIndex]
            for (let i = 0; i < parentNode.childNodes.length; i++) {
              if (parentNode.childNodes[i] === currentNode) {
                return {
                  node: parentNode,
                  offset: currentOffset + 1
                }
              }
              currentOffset++
            }
          }
        } else {
          currentVisualOffset -= currentNodeLength
        }
        searchPath[tailIndex] = currentNode.nextSibling
      }
    }
    // offset overflow - set it to the end
    return {
      node: rootNode,
      offset: rootNode.childNodes.length
    }
  } else {
    return {
      node: rootNode,
      offset: visualOffset
    }
  }
}

export function setSelectionAtEnd(contentEditableEl) {
  try {
    const nodeDocument = null == contentEditableEl ? document : contentEditableEl.ownerDocument
    const nodeWindow = null == nodeDocument ? window : nodeDocument.defaultView
    const range = nodeDocument.createRange()
    range.selectNodeContents(contentEditableEl)
    range.collapse(false)
    const selection = nodeWindow.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  } catch {
    //
  }
}
